import React, { useState } from 'react';
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Select,
    Text,
    Alert,
    AlertIcon,
    AlertDescription,
    Badge,
    Flex,
    Box,
    UnorderedList,
    ListItem,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { MdUpdate, MdLock } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import axiosClient from '../axios-client';
import { useStateContext } from '../context/ContextProvider';

/**
 * BulkStatusUpdateModal
 *
 * Props:
 *  - statusField    {string}   Django model field name to update (e.g. "status_confirmation")
 *  - statusOptions  {Array}    [{value, label}] list of allowed statuses shown in the modal Select
 *  - statusKey      {string}   The key on each order object to read the CURRENT status for validation
 *  - selectedRows   {Array}    IDs of selected orders
 *  - dataList       {Array}    Full list of order objects currently displayed
 *  - onSuccess      {Function} Called after a successful update
 *  - isDisabled     {boolean}  Extra disable condition
 *  - buttonLabel    {string}   Optional override for the button label
 *
 * Pipeline locking rules (enforced both here and on the backend):
 *   status_confirmation  → locked if order.status_stock     is set
 *   status_stock         → locked if order.status_delivery  is set
 *   status_delivery      → locked if order.status_facture   == 'Payé'
 *   status_facture       → always editable
 */

// ─── Helper: determine if a given order is locked for a given statusField ───
function getOrderLockReason(order, statusField) {
    switch (statusField) {
        case 'status_confirmation':
            if (order.status_stock && order.status_stock !== '')
                return `statut stock déjà défini (${order.status_stock})`;
            break;
        case 'status_stock':
            if (order.status_delivery && order.status_delivery !== '')
                return `statut livraison déjà défini (${order.status_delivery})`;
            break;
        case 'status_delivery':
            if (order.status_facture === 'Payé')
                return `facture déjà marquée comme payée`;
            break;
        default:
            break;
    }
    return null; // not locked
}

function BulkStatusUpdateModal({
    statusField,
    statusOptions = [],
    statusKey,
    selectedRows = [],
    dataList = [],
    onSuccess,
    isDisabled = false,
    buttonLabel,
}) {
    const { t } = useTranslation();
    const { user } = useStateContext();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [targetStatus, setTargetStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [lockedOrdersError, setLockedOrdersError] = useState([]); // backend locked order messages

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        if (selectedRows.length === 0) return false;

        const selectedOrders = dataList.filter(o => selectedRows.includes(o.id));

        // Rule 1 – Mixed current statuses
        const statuses = new Set(selectedOrders.map(o => o[statusKey] || ''));
        if (statuses.size > 1) {
            setValidationError(
                t('Impossible de mettre à jour : les commandes sélectionnées ont des statuts différents.')
            );
            return false;
        }

        // Rule 2 – Pipeline locking: check if any selected order is locked
        const locked = selectedOrders
            .map(o => {
                const reason = getOrderLockReason(o, statusField);
                return reason ? `Commande #${o.code} : ${reason}` : null;
            })
            .filter(Boolean);

        if (locked.length > 0) {
            setValidationError(
                t('Certaines commandes sélectionnées sont verrouillées et ne peuvent pas être modifiées :')
            );
            setLockedOrdersError(locked);
            return false;
        }

        setValidationError('');
        setLockedOrdersError([]);
        return true;
    };

    const handleOpenModal = () => {
        if (!validate()) return;
        setTargetStatus('');
        setLockedOrdersError([]);
        onOpen();
    };

    const handleClose = () => {
        setTargetStatus('');
        setValidationError('');
        setLockedOrdersError([]);
        onClose();
    };

    const handleConfirm = async () => {
        if (!targetStatus || selectedRows.length === 0) return;
        setLoading(true);
        setLockedOrdersError([]);
        try {
            await axiosClient.post('/commandes/bulk_actions/', {
                action: 'update_status',
                user_id: user.id,
                order_ids: selectedRows,
                status_field: statusField,
                status_value: targetStatus,
            });
            toast({
                description: t(`Statut mis à jour : ${targetStatus}`),
                status: 'success',
                duration: 2500,
                isClosable: true,
                position: 'bottom-right',
            });
            handleClose();
            if (onSuccess) onSuccess(targetStatus);
        } catch (error) {
            // // console.error('Bulk status update error:', error);
            const responseData = error?.response?.data;

            // ── Handle backend pipeline locking errors ──────────────────────
            if (responseData?.locked_orders?.length > 0) {
                setLockedOrdersError(responseData.locked_orders);
                // Don't close the modal; show errors inside it
            } else {
                toast({
                    description: responseData?.error || t('Erreur lors de la mise à jour du statut.'),
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            }
        }
        setLoading(false);
    };

    // Derive current common status for informational display in modal
    const selectedOrders = dataList.filter(o => selectedRows.includes(o.id));
    const currentStatuses = [...new Set(selectedOrders.map(o => o[statusKey] || t('Nouveau')))];

    return (
        <>
            {/* ── Trigger button ──────────────────────────────────────────────── */}
            <Button
                size="sm"
                colorScheme="teal"
                leftIcon={<MdUpdate />}
                isDisabled={isDisabled || selectedRows.length === 0}
                onClick={handleOpenModal}
                title={
                    isDisabled
                        ? t('Sélectionnez un statut dans le filtre pour activer')
                        : selectedRows.length === 0
                            ? t('Sélectionnez des commandes')
                            : t('Mettre à jour le statut des commandes sélectionnées')
                }
                flexShrink={0}
            >
                {buttonLabel || t('Changer statut')}
            </Button>

            {/* ── Inline validation error (mixed statuses or pipeline lock) ──── */}
            {validationError && (
                <Alert status="error" borderRadius="md" py={2} px={3} w="100%" mt={1}>
                    <AlertIcon />
                    <Box>
                        <AlertDescription fontSize="sm">{validationError}</AlertDescription>
                        {lockedOrdersError.length > 0 && (
                            <UnorderedList mt={1} fontSize="xs" pl={4}>
                                {lockedOrdersError.map((msg, i) => (
                                    <ListItem key={i}>{msg}</ListItem>
                                ))}
                            </UnorderedList>
                        )}
                    </Box>
                </Alert>
            )}

            {/* ── Confirmation Modal ──────────────────────────────────────────── */}
            <Modal isOpen={isOpen} onClose={handleClose} isCentered closeOnOverlayClick={false}>
                <ModalOverlay backdropFilter="blur(2px)" />
                <ModalContent>
                    <ModalHeader>
                        <Flex align="center" gap={2}>
                            <MdUpdate size={20} />
                            {t('Mise à jour groupée du statut')}
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody pb={4}>
                        <Text mb={2} fontSize="sm" color="gray.500">
                            {t('Commandes sélectionnées')} : <strong>{selectedRows.length}</strong>
                        </Text>

                        {currentStatuses.length === 1 && (
                            <Flex align="center" gap={2} mb={3}>
                                <Text fontSize="sm">{t('Statut actuel')} :</Text>
                                <Badge colorScheme="blue" px={2} rounded="md">
                                    {t(currentStatuses[0])}
                                </Badge>
                            </Flex>
                        )}

                        <Text mb={2} fontWeight="semibold">
                            {t('Choisissez le nouveau statut :')}
                        </Text>

                        <Select
                            placeholder={t('-- Sélectionner un statut --')}
                            value={targetStatus}
                            onChange={e => setTargetStatus(e.target.value)}
                            size="md"
                            rounded="md"
                        >
                            {statusOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {t(opt.label)}
                                </option>
                            ))}
                        </Select>

                        {targetStatus && lockedOrdersError.length === 0 && (
                            <Alert status="info" mt={3} borderRadius="md" py={2}>
                                <AlertIcon />
                                <AlertDescription fontSize="sm">
                                    {t('Les')} <strong>{selectedRows.length}</strong>{' '}
                                    {t('commandes sélectionnées seront mises à jour vers')} :{' '}
                                    <strong>{t(targetStatus)}</strong>
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* ── Locked orders error (returned by backend) ───────── */}
                        {lockedOrdersError.length > 0 && (
                            <Alert status="error" mt={3} borderRadius="md" py={2}>
                                <AlertIcon />
                                <Box>
                                    <AlertDescription fontSize="sm" fontWeight="semibold">
                                        <Flex align="center" gap={1}>
                                            <MdLock />
                                            {t('Certaines commandes sont verrouillées :')}
                                        </Flex>
                                    </AlertDescription>
                                    <UnorderedList mt={1} fontSize="xs" pl={4}>
                                        {lockedOrdersError.map((msg, i) => (
                                            <ListItem key={i}>{msg}</ListItem>
                                        ))}
                                    </UnorderedList>
                                </Box>
                            </Alert>
                        )}
                    </ModalBody>

                    <ModalFooter gap={2}>
                        <Button
                            colorScheme="teal"
                            isDisabled={!targetStatus || lockedOrdersError.length > 0}
                            isLoading={loading}
                            loadingText={t('Mise à jour...')}
                            onClick={handleConfirm}
                        >
                            {t('Confirmer')}
                        </Button>
                        <Button variant="ghost" onClick={handleClose}>
                            {t('Annuler')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

export default BulkStatusUpdateModal;
